import React, { useMemo, useState } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiSearch, FiAlertTriangle, FiPackage, FiUpload, FiCheckCircle, FiFileText } from 'react-icons/fi';
import useEntity from '../hooks/useEntity';
import Modal from '../components/ui/Modal';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { puede, puedeElegirIA } from '../utils/permisos';
import { featureFlagEnabled } from '../config/modulos';

const VACIO = {
  nombre: '',
  sku: '',
  categoria: '',
  unidad: 'ud',
  stock: 0,
  stockMinimo: 0,
  precio: 0,
  precioCompra: 0,
  iva: 21,
  activo: true,
  estado: 'publicado',
};

const FILTROS = [
  { value: 'todos', label: 'Todos' },
  { value: 'publicado', label: 'Publicados' },
  { value: 'borrador', label: 'Borradores' },
];

function extensionArchivo(nombre = '') {
  const partes = String(nombre || '').toLowerCase().split('.');
  return partes.length > 1 ? partes.pop() : '';
}

function resolverMimeImportacion(file) {
  const mime = String(file?.type || '').toLowerCase().trim();
  const ext = extensionArchivo(file?.name || '');

  if (mime === 'application/pdf' || mime === 'application/x-pdf' || ext === 'pdf') {
    return 'application/pdf';
  }
  if (mime === 'image/jpeg' || mime === 'image/jpg' || ext === 'jpg' || ext === 'jpeg') {
    return 'image/jpeg';
  }
  if (mime === 'image/png' || ext === 'png') {
    return 'image/png';
  }
  if (mime === 'image/gif' || ext === 'gif') {
    return 'image/gif';
  }
  if (mime === 'image/webp' || ext === 'webp') {
    return 'image/webp';
  }

  return mime;
}

export default function Almacen() {
  const { usuario, empresa } = useAuth();
  const [estadoVista, setEstadoVista] = useState('todos');
  const paramsEntity = useMemo(() => (estadoVista === 'todos' ? {} : { estado: estadoVista }), [estadoVista]);
  const { items, cargando, error, crear, actualizar, eliminar, cargar } = useEntity('almacen', { params: paramsEntity });
  const alertasStockActivas = featureFlagEnabled(empresa, 'stock_minimo_alertas');
  const [modal, setModal] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(VACIO);
  const [busqueda, setBusqueda] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [ajuste, setAjuste] = useState(null);
  const [cantidadAjuste, setCantidadAjuste] = useState(0);
  const [modalImportar, setModalImportar] = useState(false);
  const [archivoImportar, setArchivoImportar] = useState(null);
  const [importando, setImportando] = useState(false);
  const [importError, setImportError] = useState('');
  const [mensaje, setMensaje] = useState('');
  const [aiProveedorImportacion, setAiProveedorImportacion] = useState('groq');
  const [notificacion, setNotificacion] = useState(null);

  const puedeCrear = puede(usuario, 'almacen', 'crear');
  const puedeEditar = puede(usuario, 'almacen', 'editar');
  const puedeEliminar = puede(usuario, 'almacen', 'eliminar');
  const puedeElegirProveedorIA = puedeElegirIA(usuario);

  const mostrarNotificacion = (tipo, texto, duracion = 3200) => {
    setNotificacion({ tipo, texto });
    if (duracion > 0) {
      window.setTimeout(() => setNotificacion((actual) => (actual?.texto === texto ? null : actual)), duracion);
    }
  };

  const abrirNuevo = () => {
    setEditando(null);
    setForm(VACIO);
    setModal(true);
  };

  const abrirEditar = (p) => {
    setEditando(p);
    setForm({ ...VACIO, ...p, estado: p.estado || 'publicado' });
    setModal(true);
  };

  const num = (v) => (v === '' ? '' : Number(v));

  const onGuardar = async (e) => {
    e.preventDefault();
    setGuardando(true);
    setMensaje('');
    try {
      const datos = {
        ...form,
        stock: Number(form.stock) || 0,
        stockMinimo: Number(form.stockMinimo) || 0,
        precio: Number(form.precio) || 0,
        precioCompra: Number(form.precioCompra) || 0,
        iva: Number(form.iva) || 0,
      };
      if (editando) await actualizar(editando._id, datos);
      else await crear(datos);
      setModal(false);
      mostrarNotificacion('success', editando ? 'Producto actualizado correctamente' : 'Producto creado correctamente');
    } catch (err) {
      mostrarNotificacion('error', err.response?.data?.error || 'No se pudo guardar el producto');
    } finally {
      setGuardando(false);
    }
  };

  const onEliminar = async (p) => {
    if (!window.confirm(`¿Eliminar el producto "${p.nombre}"?`)) return;
    try {
      await eliminar(p._id);
      mostrarNotificacion('success', 'Producto eliminado');
    } catch {
      mostrarNotificacion('error', 'No se pudo eliminar');
    }
  };

  const onAjustar = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/almacen/${ajuste._id}/ajustar-stock`, { cantidad: Number(cantidadAjuste) });
      setAjuste(null);
      setCantidadAjuste(0);
      cargar();
      mostrarNotificacion('success', 'Stock ajustado correctamente');
    } catch (err) {
      mostrarNotificacion('error', err.response?.data?.error || 'No se pudo ajustar el stock');
    }
  };

  const publicarProducto = async (producto) => {
    try {
      await api.post(`/almacen/${producto._id}/publicar`);
      mostrarNotificacion('success', `"${producto.nombre}" publicado`);
      cargar();
    } catch (err) {
      mostrarNotificacion('error', err.response?.data?.error || 'No se pudo publicar');
    }
  };

  const toBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const abrirImportar = () => {
    setModalImportar(true);
    setArchivoImportar(null);
    setImportError('');
    setAiProveedorImportacion('groq');
  };

  const importarDocumento = async () => {
    if (!archivoImportar) {
      setImportError('Selecciona un PDF o una imagen primero');
      return;
    }
    setImportando(true);
    setImportError('');
    setMensaje('');
    try {
      const mimeType = resolverMimeImportacion(archivoImportar);
      if (!['application/pdf', 'image/jpeg', 'image/png', 'image/gif', 'image/webp'].includes(mimeType)) {
        setImportError('Formato no compatible. Usa PDF, PNG, JPG, GIF o WEBP');
        return;
      }

      const archivoBase64 = await toBase64(archivoImportar);
      const { data } = await api.post('/almacen/importar-documento', {
        archivoBase64,
        mimeType,
        nombreArchivo: archivoImportar.name,
        aiProvider: aiProveedorImportacion,
      });
      setModalImportar(false);
      setEstadoVista('borrador');
      await cargar();
      setMensaje(`Importados ${data?.total || 0} productos como borrador`);
    } catch (err) {
      setImportError(err.response?.data?.error || 'No se pudo importar el documento');
    } finally {
      setImportando(false);
    }
  };

  const set = (campo) => (e) => setForm((f) => ({ ...f, [campo]: e.target.value }));

  const filtrados = items.filter((p) => [p.nombre, p.sku, p.categoria, p.estado].join(' ').toLowerCase().includes(busqueda.toLowerCase()));
  const bajoMinimo = items.filter((p) => {
    if (p.estado === 'borrador') return false;
    const stock = Number(p.stock);
    const minimo = Number(p.stockMinimo);
    return Number.isFinite(stock) && Number.isFinite(minimo) && stock > 0 && minimo > 0 && stock <= minimo;
  });

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1 className="page-title">Almacén</h1>
          <p className="page-subtitle">{items.length} producto(s) · {items.filter((p) => p.estado === 'borrador').length} borrador(es)</p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {puedeCrear && <button className="btn" onClick={abrirImportar}><FiUpload /> Importar documento</button>}
          {puedeCrear && <button className="btn btn-primary" onClick={abrirNuevo}><FiPlus /> Nuevo producto</button>}
        </div>
      </div>

      <div className="tabs" style={{ marginBottom: 16 }}>
        {FILTROS.map((f) => (
          <button
            key={f.value}
            type="button"
            className={`tab ${estadoVista === f.value ? 'activo' : ''}`}
            onClick={() => setEstadoVista(f.value)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {alertasStockActivas && bajoMinimo.length > 0 && (
        <div className="alert-warning">
          <FiAlertTriangle /> {bajoMinimo.length} producto(s) por debajo del stock mínimo: {bajoMinimo.map((p) => p.nombre).join(', ')}
        </div>
      )}

      {!alertasStockActivas && (
        <div className="alert-warning">
          <FiAlertTriangle /> Esta empresa no tiene activas las alertas de stock mínimo.
        </div>
      )}

      <div className="toolbar">
        <div className="search-box">
          <FiSearch />
          <input placeholder="Buscar por nombre, SKU, categoría o estado" value={busqueda} onChange={(e) => setBusqueda(e.target.value)} />
        </div>
      </div>

      <div className="page-help-card" role="status">
        <div>
          <strong>Almacén</strong>
          <p>Usa los filtros y las alertas de stock para mantener el inventario bajo control sin perder tiempo.</p>
        </div>
        <div className="page-summary">
          <span className="page-pill">Productos: {items.length}</span>
        </div>
      </div>

      {notificacion && (
        <div className={`alert-${notificacion.tipo}`} role="status" style={{ marginBottom: 14 }}>
          {notificacion.texto}
        </div>
      )}

      {mensaje && <div className="alert-ok">{mensaje}</div>}
      {error && <div className="alert-error">{error}</div>}

      {cargando ? (
        <p className="empty">Cargando…</p>
      ) : filtrados.length === 0 ? (
        <p className="empty"><FiPackage /> No hay productos.</p>
      ) : (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr><th>Producto</th><th>SKU</th><th>Estado</th><th>Stock</th><th>Mínimo</th><th>Precio</th><th>IVA</th><th></th></tr>
            </thead>
            <tbody>
              {filtrados.map((p) => {
                const esBorrador = p.estado === 'borrador';
                const stock = Number(p.stock);
                const minimo = Number(p.stockMinimo);
                const alertar = !esBorrador && Number.isFinite(stock) && Number.isFinite(minimo) && stock > 0 && minimo > 0 && stock <= minimo;
                return (
                  <tr key={p._id} className={esBorrador ? 'fila-borrador' : (alertar ? 'fila-alerta' : '')}>
                    <td data-label="Producto">
                      <strong>{p.nombre}</strong>
                      {p.categoria && <span className="sub"> · {p.categoria}</span>}
                      {p.origenDocumentoNombre && <div className="sub"><FiFileText /> {p.origenDocumentoNombre}</div>}
                      {p.productoRelacionadoNombre && <div className="sub">Asignado a: {p.productoRelacionadoNombre}</div>}
                    </td>
                    <td data-label="SKU">{p.sku || '—'}</td>
                    <td data-label="Estado">
                      <span className={`badge ${esBorrador ? 'badge-warning' : 'badge-ok'}`}>{esBorrador ? 'Borrador' : 'Publicado'}</span>
                    </td>
                    <td data-label="Stock">
                      <span className={`badge ${alertar ? 'badge-danger' : 'badge-ok'}`}>{p.stock} {p.unidad}</span>
                    </td>
                    <td data-label="Mínimo">{p.stockMinimo}</td>
                    <td data-label="Precio">{(p.precio || 0).toFixed(2)} €</td>
                    <td data-label="IVA">{p.iva}%</td>
                    <td className="acciones">
                      {esBorrador && puedeEditar && (
                        <button className="icon-btn ok" onClick={() => publicarProducto(p)} title="Publicar">
                          <FiCheckCircle />
                        </button>
                      )}
                      {puedeEditar && <button className="icon-btn" onClick={() => abrirEditar(p)} title="Editar"><FiEdit2 /></button>}
                      {puedeEliminar && <button className="icon-btn danger" onClick={() => onEliminar(p)} title="Eliminar"><FiTrash2 /></button>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Modal abierto={modal} onCerrar={() => setModal(false)} titulo={editando ? 'Editar producto' : 'Nuevo producto'}>
        <form onSubmit={onGuardar} className="form-grid">
          <label className="field span-2"><span>Nombre *</span><input required value={form.nombre} onChange={set('nombre')} /></label>
          <label className="field"><span>SKU</span><input value={form.sku} onChange={set('sku')} /></label>
          <label className="field"><span>Categoría</span><input value={form.categoria} onChange={set('categoria')} /></label>
          <label className="field"><span>Unidad</span><input value={form.unidad} onChange={set('unidad')} /></label>
          <label className="field"><span>Stock</span><input type="number" value={num(form.stock)} onChange={set('stock')} disabled={Boolean(editando)} /></label>
          <label className="field"><span>Stock mínimo</span><input type="number" value={num(form.stockMinimo)} onChange={set('stockMinimo')} /></label>
          <label className="field"><span>Precio venta (€)</span><input type="number" step="0.01" value={num(form.precio)} onChange={set('precio')} /></label>
          <label className="field"><span>Precio compra (€)</span><input type="number" step="0.01" value={num(form.precioCompra)} onChange={set('precioCompra')} /></label>
          <label className="field"><span>IVA (%)</span><input type="number" value={num(form.iva)} onChange={set('iva')} /></label>
          <label className="field">
            <span>Estado</span>
            <input readOnly value={form.estado === 'borrador' ? 'Borrador' : 'Publicado'} />
          </label>
          {editando && <div className="alert-info span-2">El stock se ajusta desde el botón de ajuste, no desde esta edición.</div>}
          <div className="form-actions span-2">
            <button type="button" className="btn" onClick={() => setModal(false)}>Cancelar</button>
            <button type="submit" className="btn btn-primary" disabled={guardando}>{guardando ? 'Guardando…' : 'Guardar'}</button>
          </div>
        </form>
      </Modal>

      <Modal abierto={modalImportar} onCerrar={() => setModalImportar(false)} titulo="Importar productos desde documento" ancho={560}>
        <div style={{ display: 'grid', gap: 16 }}>
          <p className="muted" style={{ margin: 0 }}>
            Sube un PDF o una imagen de una factura/presupuesto. El sistema extraerá el texto, generará borradores y podrás revisarlos antes de publicarlos.
          </p>
          <label className="field">
            <span>Documento</span>
            <input
              type="file"
              accept="application/pdf,image/*"
              onChange={(e) => {
                const archivo = e.target.files?.[0] || null;
                setArchivoImportar(archivo);
                setImportError('');
              }}
            />
          </label>
          {puedeElegirProveedorIA && (
            <label className="field">
              <span>Proveedor de IA</span>
              <select value={aiProveedorImportacion} onChange={(e) => setAiProveedorImportacion(e.target.value)}>
                <option value="local">Local</option>
                <option value="groq">Groq</option>
              </select>
            </label>
          )}
          {archivoImportar && (
            <div className="alert-info">
              Archivo seleccionado: <strong>{archivoImportar.name}</strong>
            </div>
          )}
          {importError && <div className="alert-error">{importError}</div>}
          <div className="form-actions">
            <button type="button" className="btn" onClick={() => setModalImportar(false)}>Cancelar</button>
            <button type="button" className="btn btn-primary" onClick={importarDocumento} disabled={importando || !archivoImportar}>
              <FiUpload /> {importando ? 'Importando…' : 'Analizar e importar'}
            </button>
          </div>
        </div>
      </Modal>

      <Modal abierto={!!ajuste} onCerrar={() => setAjuste(null)} titulo={`Ajustar stock · ${ajuste?.nombre || ''}`} ancho={420}>
        <form onSubmit={onAjustar} className="form-grid">
          <p className="span-2 muted">Stock actual: <strong>{ajuste?.stock} {ajuste?.unidad}</strong>. Introduce una cantidad positiva (entrada) o negativa (salida).</p>
          <label className="field span-2"><span>Cantidad a ajustar</span><input type="number" value={cantidadAjuste} onChange={(e) => setCantidadAjuste(e.target.value)} autoFocus /></label>
          <div className="form-actions span-2">
            <button type="button" className="btn" onClick={() => setAjuste(null)}>Cancelar</button>
            <button type="submit" className="btn btn-primary">Aplicar ajuste</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
